import { DateTime } from 'luxon';
import { ReactNode } from 'react';

import { ThemeCategoryDescriptions } from '../constants/themeCategory';
import { NOT_APPLICABLE, NotApplicableProp } from '../forms/components/NotApplicable';
import { Colors } from '../theme';
import { Nullable } from '../util/helperTypes';
import { ApprovalStatus } from './approval';
import { Company } from './company';
import { Conviction } from './conviction';
import { Direction } from './direction';
import { Sector } from './sector';
import { User } from './user';

export enum ThemeDirection {
  LONG = 'LONG',
  SHORT = 'SHORT',
  NEUTRAL = 'NEUTRAL',
}

export const ThemeDirectionLabels = new Map([
  [ThemeDirection.LONG, 'Long'],
  [ThemeDirection.SHORT, 'Short'],
  [ThemeDirection.NEUTRAL, 'Neutral'],
]);

export enum ThemeType {
  MACRO = 'MACRO',
  SECULAR = 'SECULAR',
  EPISODIC = 'EPISODIC',
  MEGATREND = 'MEGATREND',
}

export const ThemeTypeLabels = new Map([
  [ThemeType.MACRO, 'Macro'],
  [ThemeType.SECULAR, 'Secular'],
  [ThemeType.EPISODIC, 'Episodic'],
  [ThemeType.MEGATREND, 'Megatrend'],
]);

export const ThemeCompanyImpactLabels = new Map([
  [Direction.LONG, 'Long'],
  [Direction.SHORT, 'Short'],
]);

export const ThemeCompanyOrders = new Map([
  [1, '1st Order'],
  [2, '2nd Order'],
  [3, '3rd Order'],
  [null, 'Loosely followed'],
]);

export const ThemeCompanyActionableLabels = new Map([
  [true, 'Active'],
  [false, 'Inactive'],
]);

export enum ThemeRegulatoryRisk {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export const ThemeRegulatoryRiskLabels = new Map([
  [ThemeRegulatoryRisk.LOW, 'Low'],
  [ThemeRegulatoryRisk.MEDIUM, 'Medium'],
  [ThemeRegulatoryRisk.HIGH, 'High'],
  [NOT_APPLICABLE, 'Not Applicable'],
]);

export enum ThemeReflection {
  NOT_REFLECTED = 'NOT_REFLECTED',
  SOMEWHAT = 'SOMEWHAT',
  FULLY = 'FULLY',
}

export enum ThemeCompanyType {
  STANDARD = 'STANDARD',
  LOOSELY_FOLLOWED = 'LOOSELY_FOLLOWED',
}

export const ThemeReflectionLabels = new Map([
  [ThemeReflection.NOT_REFLECTED, 'Not Reflected'],
  [ThemeReflection.SOMEWHAT, 'Somewhat'],
  [ThemeReflection.FULLY, 'Fully'],
  [NOT_APPLICABLE, 'Not Applicable'],
]);

export enum AnalystThemeStatus {
  NORMAL = 'NORMAL',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
}

export enum ThemeCategory {
  TRADING = 'TRADING',
  MONITORING = 'MONITORING',
  NON_HEDGING = 'NON_HEDGING',
  RADAR = 'RADAR',
}

export const ThemeCategoryLabels = new Map([
  [ThemeCategory.TRADING, 'Trading'],
  [ThemeCategory.MONITORING, 'Monitoring'],
  [ThemeCategory.NON_HEDGING, 'Non-Hedging'],
  [ThemeCategory.RADAR, 'Radar'],
]);

export const ThemeCategoryTooltips = new Map<ThemeCategory, ReactNode>([
  [ThemeCategory.TRADING, ThemeCategoryDescriptions.TRADING],
  [ThemeCategory.MONITORING, ThemeCategoryDescriptions.MONITORING],
  [ThemeCategory.NON_HEDGING, ThemeCategoryDescriptions.NON_HEDGING_SHORTER],
  [ThemeCategory.RADAR, ThemeCategoryDescriptions.RADAR],
]);

export interface ParentTheme {
  id: string;
  userId: string;
  name: string;
  time: DateTime;
}

export interface ParentThemeLeg {
  id: string;
  userId: string;
  userFullName: string;
  parentThemeId: string;
  name: string;
  time: DateTime;
  approvalStatus: ApprovalStatus;
  sector: Sector;
}

export interface ParentThemeDetails {
  parentTheme: ParentTheme;
  lastMonthAnalystThemeCount: number;
  lastMonthUniqueCompanyCount: number;
  createdTime: DateTime;
  pendingCreationApproval: boolean;
}

export interface PendingCreationAnalystTheme {
  userId: string;
  id: string;
  sector: Sector;
  name: string;
  status: AnalystThemeStatus;
  hasPendingChange: true;
  lastChangeSubmitter: User;
  isPendingChangeUser: boolean;
}

export interface AnalystTheme {
  id: string;
  userId: string;
  parentThemeId: string | null;
  name: string;
  tamFrom: number | null;
  tamTo: number | null;
  penetration: number;
  type: ThemeType | null;
  reflection: NotApplicableProp<ThemeReflection> | null;
  regulatoryRisk: NotApplicableProp<ThemeRegulatoryRisk> | null;
  originalTrigger: string | null;
  startDate: DateTime | null;
  supportingMaterialLink: string | null;
  time: DateTime;
  createdTime: DateTime;
  status: AnalystThemeStatus;
  tradable: boolean;
  active: boolean;
  conviction: Conviction;
  category: ThemeCategory;
  description: string;
  rationale: string | null;
  approvalStatus: ApprovalStatus;
  appLabel?: string;
  sector: Sector;
  hasPendingChange: boolean;
  isPendingArchiveStatusChange: 'archive' | 'unarchive' | null;
  isPendingDeletionChange: boolean;
  lastChangeSubmitter: User;
  createdBy: User;
  isPendingChangeUser: boolean;
}

export interface AnalystThemeDetails {
  analystTheme: AnalystTheme;
  authorName: string;
  lastMonthCompanyCount: number;
  pendingCreationApproval: boolean;
}

export interface AnalystThemeLeg {
  id: string;
  userId: string;
  userFullName: string;
  analystThemeId: string;
  parentThemeId: string;
  name: string;
  interpretation: string;
  directionOfProfit: ThemeDirection;
  active: boolean;
  tamFrom: number | null;
  tamTo: number | null;
  penetration: number;
  category: ThemeCategory;
  type: ThemeType | null;
  reflection: ThemeReflection | null;
  regulatoryRisk: ThemeRegulatoryRisk | null;
  originalTrigger: string | null;
  startDate: DateTime | null;
  supportingMaterialLink: string | null;
  time: DateTime;
  rationale: string | null;
  approvalStatus: ApprovalStatus;
  conviction: Conviction;
  status: AnalystThemeStatus;
  sector: Sector;
}

export type ThemeCompanyState = {
  analystThemeId: string;
  companyId: string;
  impact: Direction;
  actionable: boolean; // deprecated
  active: boolean;
  time: DateTime;
  createdTime: DateTime;
  lastChangeSubmitter: User;
  createdBy: User;
} & (
  | {
      type: ThemeCompanyType.STANDARD;
      order: number;
      rank: number;
    }
  | {
      type: ThemeCompanyType.LOOSELY_FOLLOWED;
      order: null;
      rank: null;
    }
);

export type ThemeCompanyStateTransition = {
  id: string;
  userFullName: string;
  analystThemeId: string;
  companyId: string;
  time: DateTime;
  approvalStatus: ApprovalStatus;
} & (
  | ({
      impact: Direction;
      actionable: boolean; // deprecated
      active: boolean;
      removed: false;
    } & (
      | {
          type: ThemeCompanyType.STANDARD;
          order: number;
          rank: number;
          rankExplicit: boolean;
        }
      | {
          type: ThemeCompanyType.LOOSELY_FOLLOWED;
          order: null;
          rank: null;
          rankExplicit: null;
        }
    ))
  | {
      type: null;
      impact: null;
      order: null;
      rank: null;
      rankExplicit: null;
      actionable: null; // deprecated
      active: null;
      removed: true;
    }
);

export type ThemeCompanyActiveChangeTransition = {
  id: string;
  userFullName: string;
  analystThemeId: string;
  companyId: string;
  type: ThemeCompanyType.STANDARD | ThemeCompanyType.LOOSELY_FOLLOWED;
  impact: Direction;
  active: boolean;
  time: DateTime;
  order: null;
  rank: null;
  rankExplicit: null;
  removed: true;
};

export interface ParentThemeInput {
  name: string;
  sectorId: string;
}

export interface AnalystThemeInput {
  parentThemeId: string | null;
  name: string;
  description: string | null;
  status: AnalystThemeStatus;
  tradable: boolean;
  conviction: Conviction;
  rationale: string | null;
  tamFrom: number | null;
  tamTo: number | null;
  penetration: number;
  type: ThemeType | null;
  category: ThemeCategory;
  reflection: NotApplicableProp<ThemeReflection> | null;
  regulatoryRisk: NotApplicableProp<ThemeRegulatoryRisk> | null;
  originalTrigger: string | null;
  startDate: string | null;
  supportingMaterialLink: string | null;
  sectorId: string;
  themeCompanyStateInputs: ThemeCompanyStateInput[] | null;
}

export type ThemeCompanyStandardStateInput = {
  companyId: string;
  impact: Direction;
  active: boolean;
  type: ThemeCompanyType.STANDARD;
  order: number;
  rank: number;
  rankExplicit: boolean;
};

export type ThemeCompanyLooseStateInput = {
  companyId: string;
  impact: Direction;
  active: boolean;
  type: ThemeCompanyType.LOOSELY_FOLLOWED;
  order: null;
  rank: null;
  rankExplicit: null;
};

export type ThemeCompanyStateInput = ThemeCompanyStandardStateInput | ThemeCompanyLooseStateInput;

export type ThemeChangeRationale = { rationale?: string | null };

export type ArchiveThemeJSON = {
  status: Exclude<AnalystThemeStatus, AnalystThemeStatus.DELETED>;
} & ThemeChangeRationale;

export type ArchiveThemeParams = {
  themeName: string;
  analystThemeId: string;
  parentThemeId?: string | null;
  status: Exclude<AnalystThemeStatus, AnalystThemeStatus.DELETED>;
} & ThemeChangeRationale;

export type DeleteThemeParams = {
  themeName: string;
  analystThemeId: string;
  parentThemeId?: string | null;
} & ThemeChangeRationale;

export type PendingThemeChangeParams = {
  analystThemeId: string;
  parentThemeId?: Nullable<string>;
};

export type PendingThemeChangeActionMutationOptions = {
  closeDrawer?: () => void;
};

export const ThemeImpactAndOrderAccentColors = new Map([
  [JSON.stringify([Direction.LONG, 1]), Colors.Green[3]],
  [JSON.stringify([Direction.LONG, 2]), Colors.Green[101]],
  [JSON.stringify([Direction.LONG, 3]), Colors.Green[102]],
  [JSON.stringify([Direction.SHORT, 1]), Colors.Red[3]],
  [JSON.stringify([Direction.SHORT, 2]), Colors.Red[101]],
  [JSON.stringify([Direction.SHORT, 3]), Colors.Red[102]],
  [JSON.stringify([Direction.LONG, null]), Colors.Gray[4]],
  [JSON.stringify([Direction.SHORT, null]), Colors.Gray[4]],
]);

export const formatPenetration = (penetration: number): string =>
  penetration !== 0 ? `${penetration}` : 'Not Applicable';

export const formatTam = (tamFrom: number | null, tamTo: number | null): string => {
  if (tamFrom === null && tamTo === null) {
    return 'None';
  }
  if (tamFrom === null && tamTo !== null) {
    return `${tamTo}`;
  }
  if (tamFrom !== null && tamTo === null) {
    return `${tamFrom}`;
  }
  if (tamFrom !== null && tamTo !== null) {
    return `${tamFrom}–${tamTo}`;
  }
  throw new Error('This should never be reached');
};

export interface ThemePendingChangeSubmissionDetails {
  id: string;
  currentApprovedParentTheme?: ParentTheme;
  pendingParentTheme?: ParentTheme;
  currentApprovedAnalystTheme?: AnalystTheme;
  pendingAnalystTheme: AnalystTheme;
  currentApprovedThemeCompanies: ThemeCompanyState[];
  pendingThemeCompanies: ThemeCompanyState[];
  companies: Company[];
  pendingRationale?: Nullable<string>;
}
